import { PhoneNumber } from "univapay-node";

const zoneOneAreaCodeMap: Record<string, string> = {
    "BB": "1246",
    "BM": "1441",
    "JM": "1876",
    "BS": "1242",
    "GU": "1671",
    "GD": "1473",
    "MP": "1670",
    "MS": "1664",
    "VG": "1284",
    "DM": "1767",
    "LC": "1758",
    "KN": "1869",
    "TT": "1868",
    "VC": "1784",
    "TC": "1649",
    "VI": "1340",
    "AS": "1684",
};

const zoneOneAreaCodes = new Set(Object.values(zoneOneAreaCodeMap));
const REGION_CODE_LENGTH = 3;

/**
 * Validates a phone number object.
 *
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {boolean} - Returns false if the phone number is valid, otherwise true.
 *
 * Invalid if:
 * - The local number contains other characters than digits and spaces
 * - The local number (not included spaces) has a length less than 4
 */
export const validatePhoneNumber = (phoneNumber: PhoneNumber) => {
    const localNumber = phoneNumber?.localNumber?.replace(/ /g, "");

    if (!phoneNumber || !localNumber) {
        return false;
    }

    if (!/^[0-9]+$/.test(localNumber)) {
        return true;
    }

    return localNumber.length < 4 || localNumber.length > 15;
};

/**
 * Formats a phone number object.
 *
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {PhoneNumber} - Returns a phone number with the localNumber spaces removed.
 *
 */
export const formatPhoneNumber = (phoneNumber: PhoneNumber) => {
    const formatted = phoneNumber?.localNumber?.replace(/\s+/g, "");

    const formattedNumber = {
        countryCode: phoneNumber?.countryCode || (formatted ? "81" : undefined),
        localNumber: formatted,
    };

    return formatPhoneNumberToAPI(formattedNumber);
};

/**
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {PhoneNumber} - Returns formatted zone one phone number from client to API format
 * (API expects region code to be prepended to local number)
 * Country code becomes "1" and rest is prepended to the local number
 * BE limits the country code to 3 digits, thus we need to handle it on the client side
 */
export const formatPhoneNumberToAPI = (phoneNumber: PhoneNumber): PhoneNumber => {
    if (zoneOneAreaCodes.has(phoneNumber?.countryCode)) {
        const areaCode = phoneNumber.countryCode.slice(-REGION_CODE_LENGTH);
        return {
            countryCode: "1",
            localNumber: `${areaCode}${phoneNumber.localNumber}`,
        };
    }
    return phoneNumber;
};

/**
 * @param {PhoneNumber} phoneNumber - The phone number object to validate.
 * @returns {PhoneNumber} - Returns a formatted zone one phone number to client format
 * Removes first three digits from local number and appends it to the country code
 */
export const formatPhoneNumberToClient = (phoneNumber: PhoneNumber): PhoneNumber => {
    const areaCode = phoneNumber?.localNumber?.slice(0, REGION_CODE_LENGTH); // first 3 digits from local number
    const combinedCode = `1${areaCode}`;
    if (zoneOneAreaCodes.has(combinedCode)) {
        return {
            countryCode: combinedCode,
            localNumber: phoneNumber.localNumber.slice(REGION_CODE_LENGTH),
        };
    }

    return phoneNumber;
};
